import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR, formatDate } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileText, Package, Receipt, Archive as ArchiveIcon } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICONS = { quote: FileText, inventory: Package, proforma: Receipt };

export default function Archive() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchArchive = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter && typeFilter !== "all") params.set("doc_type", typeFilter);
      const { data } = await api.get(`/archive?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento archivio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArchive(); }, [search, typeFilter]);

  const handleClick = (item) => {
    if (item.doc_type === "quote") navigate(`/quotes/${item.id}`);
    else if (item.doc_type === "inventory") navigate(`/inventories/${item.id}`);
    else if (item.doc_type === "proforma") navigate(`/proformas/${item.id}`);
  };

  return (
    <div data-testid="archive-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Archivio</h1>
          <p className="text-sm text-zinc-500">{total} documenti archiviati</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            data-testid="archive-search"
            placeholder="Cerca per numero o oggetto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="archive-type-filter" className="w-[200px] rounded-sm">
            <SelectValue placeholder="Tutti i tipi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="quote">Preventivi</SelectItem>
            <SelectItem value="inventory">Inventari</SelectItem>
            <SelectItem value="proforma">Fatture Proforma</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Tipo</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Numero</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Cliente</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Data</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Stato</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Totale</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const Icon = TYPE_ICONS[item.doc_type] || FileText;
                return (
                  <tr
                    key={`${item.doc_type}-${item.id}`}
                    onClick={() => handleClick(item)}
                    className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer transition-colors"
                    data-testid={`archive-row-${item.id}`}
                  >
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 bg-zinc-100 rounded-sm px-2 py-1">
                        <Icon className="w-3 h-3" /> {item.doc_type_label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm font-mono">{item.number || "BOZZA"}</td>
                    <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell">{item.customer_name || "-"}</td>
                    <td className="px-5 py-3 text-sm text-zinc-600 hidden sm:table-cell">{formatDate(item.date)}</td>
                    <td className="px-5 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-5 py-3 text-sm text-right font-medium">{item.total != null ? formatEUR(item.total) : "-"}</td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <ArchiveIcon className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
                    <p className="text-sm text-zinc-400">Nessun documento archiviato</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
